import { cn } from '@/lib/utils';

/**
 * Marca del producto: **SellProBot**.
 *
 * El logo vive en `public/` en dos piezas:
 *  - `logo-mark.svg` — el isotipo (robot + burbuja + flecha), fondo transparente. Es lo que se
 *    usa en la app.
 *  - `logo.svg` — el lockup completo (fondo oscuro + isotipo + wordmark + lema), el asset
 *    oficial para piezas externas (presentaciones, redes, correo).
 *
 * Aquí NO se usa el wordmark del SVG: el nombre se compone con el texto REAL de la app. Así sale
 * con la tipografía del proyecto (Inter, la de `styles/index.css`), hereda los tokens de color y
 * de tamaño, se puede truncar en el sidebar y el lector de pantalla lo lee como texto (el isotipo
 * va decorativo, con `alt=""`).
 *
 * El isotipo va dentro de un cuadro oscuro (`bg-ink`): su casco es blanco y sobre el blanco del
 * sidebar se perdería; con el cuadro se ve igual que en el logo, que es lo que la referencia
 * visual pide (cuadro de color + wordmark).
 */

export type BrandSize = 'sm' | 'md' | 'lg';

/** Cuadro que enmarca el isotipo. */
const MARK_BOX: Record<BrandSize, string> = {
  sm: 'size-9 rounded-control',
  md: 'size-11 rounded-panel',
  lg: 'size-16 rounded-card',
};

/** Alto del isotipo dentro del cuadro (deja aire alrededor para que respire). */
const MARK_IMAGE: Record<BrandSize, string> = {
  sm: 'h-7',
  md: 'h-8',
  lg: 'h-12',
};

/** Peso y tamaño del wordmark en cada contexto. */
const WORDMARK: Record<BrandSize, string> = {
  sm: 'text-card-title font-semibold',
  md: 'text-card-title font-bold',
  lg: 'text-page font-bold',
};

export interface BrandMarkProps {
  size?: BrandSize;
  className?: string;
}

/** Isotipo solo (el cuadro oscuro con el robot), sin el nombre. */
export function BrandMark({ size = 'sm', className }: BrandMarkProps) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center bg-ink',
        MARK_BOX[size],
        className,
      )}
    >
      <img src="/logo-mark.svg" alt="" className={cn('w-auto', MARK_IMAGE[size])} />
    </span>
  );
}

export interface BrandLogoProps {
  size?: BrandSize;
  /** Añade el lema bajo el nombre (el que trae el logo: el de la página pública). */
  tagline?: boolean;
  className?: string;
}

/** Marca completa: isotipo + nombre («SellPro» en tinta y «Bot» en el azul de marca). */
export function BrandLogo({ size = 'sm', tagline = false, className }: BrandLogoProps) {
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <BrandMark size={size} />

      <div className="min-w-0">
        <span className={cn('block truncate text-ink', WORDMARK[size])}>
          SellPro
          <span className="text-brand-600">Bot</span>
        </span>

        {tagline ? (
          <span className="mt-0.5 block text-micro font-medium tracking-[0.18em] text-ink-faint uppercase">
            Tu chatbot de ventas en automático
          </span>
        ) : null}
      </div>
    </div>
  );
}
